import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AddCircleOutline,
  RestartAltOutlined,
  RuleOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { isArray, isEmpty, size } from "lodash";
import isNil from "lodash/isNil";
import useLocalStorageState from "use-local-storage-state";

import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import SearchInput from "../../../../components/inputs/SearchInput";
import Flx from "../../../../components/layout/Flx";
import FilterTabSwitcher from "../../../../components/navigation/FilterTabSwitcher";
import LoanUnderwritingConditionsTable, {
  CONDITIONS_TABLE_COLUMN_STATE_KEY,
} from "../../../../components/tables/LoanUnderwritingConditionsTable";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataUwRequirementsSet } from "../../../../redux/reducers/loanFetchedDataReducer";

const LoanDrilldownConditionsTab = ({ headerEndContent }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const [quickFilter, setQuickFilter] = useState(null);
  const { loading, getLoanUnderwritingRequirements } = useChecklistRouteHook();
  const [tab, setTab] = useState("All");
  // Mirror of the table's remembered-view key (shared via useLocalStorageState)
  // so we can surface a Reset button only when the user has a saved layout.
  const [savedTableState, setSavedTableState] = useLocalStorageState(
    CONDITIONS_TABLE_COLUMN_STATE_KEY,
    { defaultValue: null },
  );
  const hasSavedTableState =
    isArray(savedTableState) && savedTableState.length > 0;
  const tabs = useMemo(
    () => buildStatusFilterTabs(uwRequirements),
    [uwRequirements],
  );

  const filteredConditions = useMemo(() => {
    if (tab === "All") {
      return uwRequirements;
    }
    return uwRequirements?.filter((c) => c.status === tab);
  }, [tab, uwRequirements]);

  const onAdd = useCallback(() => {
    dispatch(
      sidebarTypeSet(
        loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
      ),
    );
    dispatch(sidebarSetStateValue(null));
  }, [dispatch]);

  const onEditClick = useCallback(
    (data) => {
      dispatch(
        sidebarTypeSet(
          loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
        ),
      );
      dispatch(sidebarSetStateValue(data));
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      isNil(uwRequirements) ||
      (!isArray(uwRequirements) && !isNil(loan?._id))
    ) {
      getLoanUnderwritingRequirements({
        dealId: loan?._id,
        onSuccessFn: (data) => {
          dispatch(loanFetchedDataUwRequirementsSet(data));
        },
      });
    }
  }, [uwRequirements]);

  if (!loading && !isNil(uwRequirements) && isEmpty(uwRequirements)) {
    return (
      <Flx column gap={1.5} sx={{ p: 2 }}>
        <EmptyStatePlaceholder
          icon={<RuleOutlined />}
          title="No conditions yet"
          description="Add the first condition, stip, or missing document requirement for this loan."
          actions={
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={onAdd}
            >
              Add Condition
            </Button>
          }
        />
      </Flx>
    );
  }

  return (
    <LoanTabShell
      maxWidth={false}
      title={
        isNil(uwRequirements)
          ? "Loan conditions"
          : `Loan conditions (${size(uwRequirements)})`
      }
      subTitle="Click on any of the following condition cards to edit."
      meta={<RefreshConditionsButton />}
      secondaryActions={headerEndContent ? [headerEndContent] : undefined}
      primaryAction={{
        label: "Add condition",
        icon: <AddCircleOutline />,
        onClick: onAdd,
      }}
    >
      {loading ? (
        <Flx center sx={{ py: 8 }}>
          <CircularProgress size={24} />
        </Flx>
      ) : (
        <Flx
          column
          sx={{
            overflow: "hidden",
            borderRadius: 1,
            p: 2,
            border: "1px solid #e2e8f0",
            background: "#fff",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
          }}
        >
          <Flx g={1} fw jb ac wrap sx={{ mb: 2 }}>
            <Flx g={1} ac>
              <SearchInput value={quickFilter} onChange={setQuickFilter} />

              {hasSavedTableState && (
                <Tooltip
                  title="Reset the table to its default layout"
                  placement="top"
                >
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<RestartAltOutlined />}
                    onClick={() => setSavedTableState(null)}
                  >
                    Reset Table View
                  </Button>
                </Tooltip>
              )}
            </Flx>
            <FilterTabSwitcher
              value={tab}
              fontSize={11}
              sx={{
                borderColor: "divider",
                borderWidth: 1,
                borderStyle: "solid",
                borderRadius: 1,
              }}
              onChange={setTab}
              tabs={tabs}
            />
          </Flx>
          <LoanUnderwritingConditionsTable
            rowData={filteredConditions}
            quickFilter={quickFilter}
            onRowClicked={onEditClick}
          />
        </Flx>
      )}
    </LoanTabShell>
  );
};

const RefreshConditionsButton = () => {
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const dispatch = useDispatch();
  const onClick = () => {
    dispatch(loanFetchedDataUwRequirementsSet(null));
  };

  return (
    <Tooltip title="Refresh Conditions" placement="top">
      <IconButton
        size="small"
        loading={isNil(uwRequirements)}
        onClick={onClick}
      >
        <SyncOutlined />
      </IconButton>
    </Tooltip>
  );
};

const buildStatusFilterTabs = (conditions = []) => {
  if (isNil(conditions)) return [];
  const counts = {};
  conditions.forEach((c) => {
    const status = c.status || "Unknown";
    counts[status] = (counts[status] || 0) + 1;
  });

  const tabs = [{ label: "All", value: "All", count: conditions.length }];
  Object.entries(counts).forEach(([status, count]) => {
    tabs.push({ label: status, value: status, count });
  });

  return tabs;
};

export default LoanDrilldownConditionsTab;
